import { useEffect, useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';

import CropFreeIcon           from '@mui/icons-material/CropFree';
import ImageOutlinedIcon      from '@mui/icons-material/ImageOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

import { apiFetch, friendlyMessage } from '../../api/apiClient';
import { fetchCombinations } from './SpacePlanning/api';
import { LoadingState, ErrorState, InlineAlert } from '../../shared/components/status';
import { GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, WARNING, labelMono, bodyFont } from '../../theme/tokens2';

// ─── Local tokens ─────────────────────────────────────────────────────────────
const GREEN_FAINT  = '#f4fff7';
const GREEN_LIGHT  = 'rgba(33,131,51,0.25)';
const GREEN_SUBTLE = 'rgba(33,131,51,0.06)';

// ─── Arrow SVG ────────────────────────────────────────────────────────────────
function Arrow({ color = GREEN, size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ children }) {
  return (
    <Box sx={{
      display: 'inline-block',
      ...labelMono, fontSize: '9px',
      background: GREEN_FAINT, color: GREEN,
      px: '7px', py: '2px',
      border: `1px solid ${GREEN_LIGHT}`,
    }}>
      {children}
    </Box>
  );
}

// ─── CostBar ──────────────────────────────────────────────────────────────────
function CostBar({ label, value, maxValue }) {
  const pct = maxValue > 0 && value != null ? Math.round((value / maxValue) * 100) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '5px' }}>
      <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, width: 72, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box aria-hidden sx={{ flex: 1, height: 3, backgroundColor: BORDER }}>
        <Box sx={{ height: 3, width: `${pct}%`, backgroundColor: GREEN }} />
      </Box>
      <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, width: 38, textAlign: 'right', flexShrink: 0 }}>
        {value != null ? `$${(value / 1000).toFixed(0)}k` : 'N/A'}
      </Typography>
    </Box>
  );
}

// ─── AdviceItem ───────────────────────────────────────────────────────────────
// Tips shown before publishing. Advice only — nothing is tracked.
const BRIEF_ADVICE = [
  {
    Icon: CropFreeIcon,
    label: 'Space Planning',
    advice: "Make sure your layout accounts for everyone who'll live here day-to-day. Designers will build on what you've selected, so the closer it reflects real life, the better their proposals will be.",
  },
  {
    Icon: ImageOutlinedIcon,
    label: 'Moodboard',
    advice: "The more specific your images, the more targeted the proposals. If you've pinned a material or finish you love, note why — designers respond well to the reasoning, not just the reference.",
  },
  {
    Icon: AssignmentOutlinedIcon,
    label: 'Questionnaire',
    advice: "Scan your answers one more time. Anything you'd want a designer to know that isn't captured there — lifestyle quirks, deal-breakers, budget sensitivities — is worth adding before they start.",
  },
];

function AdviceItem({ Icon, label, advice }) {
  return (
    <Box sx={{
      display: 'flex', gap: '12px', alignItems: 'flex-start',
      px: '12px', py: '14px',
      border: `1px solid ${BORDER}`,
      backgroundColor: '#fff',
    }}>
      <Box aria-hidden sx={{
        width: 28, height: 28, flexShrink: 0,
        border: `0.5px solid ${GREEN_LIGHT}`,
        backgroundColor: GREEN_SUBTLE,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mt: '1px',
      }}>
        <Icon sx={{ fontSize: '0.8rem', color: GREEN }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: CHARCOAL, mb: '4px', ...bodyFont }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: MUTED, lineHeight: 1.6, ...bodyFont }}>
          {advice}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── STATE 1: Pre-tender ──────────────────────────────────────────────────────
function PreTenderState({ onRequestPublish }) {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' },
      border: `0.5px solid ${BORDER}`,
    }}>

      {/* Left: advice panel */}
      <Box sx={{
        backgroundColor: '#fff',
        p: { xs: 3, md: '24px' },
        borderRight: { xs: 'none', lg: `0.5px solid ${BORDER}` },
        borderBottom: { xs: `0.5px solid ${BORDER}`, lg: 'none' },
      }}>
        <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, mb: '10px' }}>
          Before you publish
        </Typography>
        <Typography component="h1" sx={{
          fontSize: '22px', fontWeight: 300, letterSpacing: '-0.025em',
          lineHeight: 1.1, color: CHARCOAL, mb: '10px', ...bodyFont,
        }}>
          A few things to{' '}
          <Box component="span" sx={{ fontStyle: 'italic', color: GREEN }}>check first.</Box>
        </Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, mb: '20px', ...bodyFont }}>
          The more complete your brief, the sharper the proposals you'll receive.
          Take a moment to review each section before inviting firms.
        </Typography>

        <Box sx={{ height: '0.5px', backgroundColor: BORDER, mb: '20px' }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {BRIEF_ADVICE.map((item) => (
            <AdviceItem key={item.label} {...item} />
          ))}
        </Box>
      </Box>

      {/* Right: publish panel */}
      <Box sx={{ backgroundColor: GREEN_FAINT, p: { xs: 3, md: '24px' } }}>
        <Typography sx={{ ...labelMono, fontSize: '9px', color: 'rgba(33,131,51,0.70)', mb: '10px' }}>
          Publish tender
        </Typography>
        <Typography component="h2" sx={{
          fontSize: '22px', fontWeight: 300, letterSpacing: '-0.025em',
          lineHeight: 1.1, color: CHARCOAL, mb: '6px', ...bodyFont,
        }}>
          Send your brief to{' '}
          <Box component="span" sx={{ fontStyle: 'italic', color: GREEN }}>shortlisted firms.</Box>
        </Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, mb: '22px', ...bodyFont }}>
          Once published, interior designers and contractors will review your space planning
          preferences and moodboard, then submit structured proposals for you to compare.
        </Typography>

        {/* What happens next */}
        <Box sx={{ border: `1px solid ${GREEN_LIGHT}`, backgroundColor: '#fff', p: '20px' }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: GREEN, mb: '14px' }}>
            What happens next
          </Typography>

          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {[
              'Your brief is shared with a curated list of firms.',
              'Firms submit structured proposals using a standardised format.',
              'You compare bids side-by-side here, with full cost breakdowns.',
            ].map((text, i) => (
              <Box component="li" key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', mb: i < 2 ? '10px' : 0 }}>
                <Box aria-hidden sx={{
                  width: 18, height: 18, border: `1px solid ${GREEN_LIGHT}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, mt: '1px',
                }}>
                  <Typography component="span" sx={{ ...labelMono, fontSize: '8px', color: GREEN }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: MUTED, lineHeight: 1.5, ...bodyFont }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ height: '0.5px', backgroundColor: 'rgba(33,131,51,0.20)', my: '18px' }} />

          {/* Publish button: opens the confirm box, doesn't publish by itself */}
          <Box
            component="button"
            type="button"
            onClick={onRequestPublish}
            aria-haspopup="dialog"
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%',
              backgroundColor: GREEN,
              border: 'none', p: '13px 16px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: GREEN_HOVER },
              '&:focus-visible': { outline: `2px solid ${CHARCOAL}`, outlineOffset: '2px' },
            }}
          >
            <Typography component="span" sx={{ ...labelMono, fontSize: '11px', color: '#fff' }}>
              Publish my tender
            </Typography>
            <Arrow color="#fff" size={14} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── PublishConfirmDialog ─────────────────────────────────────────────────────
// Publishing can't be undone, so "Publish my tender" first opens this box.
// It shows what will be sent, so the customer can spot an empty section.
// Empty sections are shown in amber as a gentle warning; they don't block
// publishing. "Not yet, keep editing" has the keyboard focus, so an
// accidental Enter doesn't publish.

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// A Questionnaire answer counts if it's a non-empty text or `true`.
const isAnswered = (v) => (typeof v === 'string' ? v.trim() !== '' : v === true || typeof v === 'number');

const SINGLE_QUESTIONS = [
  'heavycooking', 'altarspace',
  'lifestyle_hosting', 'lifestyle_noise', 'lifestyle_morningsync', 'lifestyle_maintenance',
  'budget', 'dream_feeling', 'dream_inspiration', 'dream_fix', 'dream_dislikes', 'notes',
];
// Each group of tiles counts once if any tile in it is picked.
const TILE_GROUPS = ['pets_', 'kitchen_', 'activityrm_', 'bulky_', 'future_'];

function countAnswered(profile, occupants) {
  if (!profile) return occupants?.length || 0;
  const singles = SINGLE_QUESTIONS.filter((key) => isAnswered(profile[key])).length;
  const groups  = TILE_GROUPS.filter((prefix) =>
    Object.keys(profile).some((key) => key.startsWith(prefix) && isAnswered(profile[key]))
  ).length;
  return (occupants?.length || 0) + singles + groups;
}

// Each row: { text, empty } once loaded; null while loading; 'error' if it failed.
async function loadPublishSummary() {
  const [layouts, photos, profile, occupants] = await Promise.allSettled([
    fetchCombinations(),
    apiFetch('/api/scenes/my'),
    apiFetch('/api/householdprofile/my'),
    apiFetch('/api/occupants/my'),
  ]);

  const layoutRow = layouts.status !== 'fulfilled' ? 'error' : (() => {
    const n = (layouts.value || []).length;
    return n ? { text: `${plural(n, 'layout')}, in your ranked order`, empty: false } : { text: 'No layouts yet', empty: true };
  })();

  const photoRow = photos.status !== 'fulfilled' ? 'error' : (() => {
    const list  = photos.value || [];
    const rooms = new Set(list.map((p) => p.cat_room)).size;
    return list.length
      ? { text: `${plural(list.length, 'photo')} across ${plural(rooms, 'room')}`, empty: false }
      : { text: 'No photos yet', empty: true };
  })();

  const questionRow = (profile.status !== 'fulfilled' || occupants.status !== 'fulfilled') ? 'error' : (() => {
    const n = countAnswered(profile.value, occupants.value);
    return n ? { text: `${plural(n, 'question')} answered`, empty: false } : { text: 'Not started', empty: true };
  })();

  return { layouts: layoutRow, photos: photoRow, questions: questionRow };
}

function SummaryRow({ label, value, last }) {
  const text  = value == null ? '…' : value === 'error' ? '—' : value.text;
  const empty = value && value !== 'error' && value.empty;
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2,
      px: '14px', py: '10px',
      borderBottom: last ? 'none' : `0.5px solid ${BORDER}`,
    }}>
      <Typography component="dt" sx={{ fontSize: '12px', fontWeight: 500, color: CHARCOAL, ...bodyFont }}>
        {label}
      </Typography>
      <Typography component="dd" sx={{ m: 0, fontSize: '12px', textAlign: 'right', color: empty ? WARNING : MUTED, ...bodyFont }}>
        {text}
      </Typography>
    </Box>
  );
}

function PublishConfirmDialog({ open, onCancel, onConfirm, publishing, publishError }) {
  const [summary, setSummary] = useState({ layouts: null, photos: null, questions: null });

  // Fresh numbers every time the box opens
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSummary({ layouts: null, photos: null, questions: null });
    loadPublishSummary().then((s) => { if (!cancelled) setSummary(s); });
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={publishing ? undefined : onCancel}   // Esc / outside click = "Not yet", except while publishing
      aria-labelledby="publish-title"
      aria-describedby="publish-body"
      PaperProps={{ sx: { width: 480, maxWidth: 'calc(100% - 32px)', m: 2, borderRadius: 0 } }}
    >
      <Box sx={{ p: { xs: '20px', sm: '28px' } }}>
        <Typography id="publish-title" component="h2" sx={{
          fontSize: '22px', fontWeight: 300, letterSpacing: '-0.025em',
          lineHeight: 1.15, color: CHARCOAL, mb: '14px', ...bodyFont,
        }}>
          Publish your tender?
        </Typography>

        <Box id="publish-body">
          <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, mb: '10px', ...bodyFont }}>
            Your brief will be sent to our network of designers and firms, who will prepare quotes based on it.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.6, mb: '20px', ...bodyFont }}>
            You can keep updating your brief afterwards, but firms will work from the version sent now.
          </Typography>
        </Box>

        <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, mb: '8px' }}>
          What will be sent
        </Typography>
        <Box component="dl" aria-live="polite" sx={{ m: 0, border: `1px solid ${BORDER}`, mb: '20px' }}>
          <SummaryRow label="Space Planning" value={summary.layouts} />
          <SummaryRow label="Moodboard"      value={summary.photos} />
          <SummaryRow label="Questionnaire"  value={summary.questions} last />
        </Box>

        {publishError && (
          <Box sx={{ mb: '16px' }}>
            <InlineAlert severity="error">{publishError}</InlineAlert>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
          <Box
            component="button"
            type="button"
            autoFocus
            onClick={onCancel}
            disabled={publishing}
            sx={{
              ...labelMono, fontSize: '10px', color: CHARCOAL,
              background: '#fff', border: `1px solid ${BORDER}`,
              px: '16px', minHeight: 42,
              cursor: publishing ? 'not-allowed' : 'pointer',
              opacity: publishing ? 0.5 : 1,
              '&:hover': { borderColor: CHARCOAL },
              '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
            }}
          >
            Not yet, keep editing
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onConfirm}
            disabled={publishing}
            sx={{
              display: 'flex', alignItems: 'center', gap: '10px',
              ...labelMono, fontSize: '10px', color: '#fff',
              background: publishing ? 'rgba(33,131,51,0.45)' : GREEN,
              border: 'none', px: '16px', minHeight: 42,
              cursor: publishing ? 'progress' : 'pointer',
              '&:hover': { background: publishing ? 'rgba(33,131,51,0.45)' : GREEN_HOVER },
              '&:focus-visible': { outline: `2px solid ${CHARCOAL}`, outlineOffset: '2px' },
            }}
          >
            {publishing ? 'Publishing…' : 'Publish my tender'}
            {!publishing && <Arrow color="#fff" size={13} />}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

// ─── STATE 2: Tender published, awaiting bids ─────────────────────────────────
function AwaitingBidsState() {
  return (
    <Box sx={{
      border: `1px dashed ${BORDER}`,
      p: { xs: 3, md: '28px 20px' }, backgroundColor: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    }}>
      <Typography component="h1" sx={{ fontSize: '13px', fontWeight: 500, color: CHARCOAL, ...bodyFont }}>
        Your tender is live
      </Typography>
      <Typography sx={{
        fontSize: '12px', color: MUTED, textAlign: 'center',
        maxWidth: 300, lineHeight: 1.55, ...bodyFont,
      }}>
        Firms are reviewing your brief. You'll be notified via WhatsApp when proposals arrive.
      </Typography>

      {/* Progress timeline */}
      <Box component="ol" sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 360, mt: '14px', listStyle: 'none', p: 0 }}>

        {/* Step 1: complete */}
        <Box component="li" sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Box aria-hidden sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: GREEN, mt: '3px' }} />
          <Box sx={{ pb: '14px', flex: 1 }}>
            <Typography sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL, mb: '2px' }}>
              Profile submitted
            </Typography>
            <Typography sx={{ fontSize: '11px', color: MUTED, lineHeight: 1.4, ...bodyFont }}>
              Your floor plan and preferences are on file.
            </Typography>
            <Typography sx={{
              ...labelMono, fontSize: '9px', color: GREEN,
              backgroundColor: GREEN_FAINT, px: '6px', py: '2px',
              display: 'inline-block', mt: '3px',
            }}>
              Complete
            </Typography>
          </Box>
        </Box>

        {/* Step 2: active */}
        <Box component="li" sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Box aria-hidden sx={{
            width: 9, height: 9, borderRadius: '50%', backgroundColor: GREEN,
            outline: '3px solid rgba(33,131,51,0.2)', mt: '3px',
          }} />
          <Box sx={{ pb: '14px', flex: 1 }}>
            <Typography sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL, mb: '2px' }}>
              Tender published
            </Typography>
            <Typography sx={{ fontSize: '11px', color: MUTED, lineHeight: 1.4, ...bodyFont }}>
              Shortlisted firms have been invited to submit proposals.
            </Typography>
            <Typography sx={{
              ...labelMono, fontSize: '9px', color: GREEN,
              backgroundColor: GREEN_FAINT, px: '6px', py: '2px',
              display: 'inline-block', mt: '3px',
            }}>
              In progress
            </Typography>
          </Box>
        </Box>

        {/* Step 3: pending */}
        <Box component="li" sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Box aria-hidden sx={{
            width: 9, height: 9, borderRadius: '50%', mt: '3px',
            backgroundColor: 'transparent',
            border: `1.5px solid ${BORDER}`,
          }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ ...labelMono, fontSize: '10px', color: MUTED, mb: '2px' }}>
              Bids received
            </Typography>
            <Typography sx={{ fontSize: '11px', color: MUTED, lineHeight: 1.4, ...bodyFont }}>
              Compare proposals side-by-side once they arrive.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── STATE 3: Bids received ───────────────────────────────────────────────────
const SORT_OPTIONS = [
  { key: 'cost',   label: 'Total cost' },
  { key: 'rating', label: 'Rating'     },
];

function BidCard({ bid, isLowestCost, isHighestRating }) {
  const total     = bid.cost_total                != null ? Number(bid.cost_total)                : null;
  const carpentry = bid.cost_carpentrycountertop  != null ? Number(bid.cost_carpentrycountertop)  : null;
  const masonry   = bid.cost_masonryflooring      != null ? Number(bid.cost_masonryflooring)      : null;
  const plumbing  = bid.cost_plumbingelectrical   != null ? Number(bid.cost_plumbingelectrical)   : null;

  const hasBadge = isLowestCost || isHighestRating;

  return (
    <Box component="article" sx={{
      backgroundColor: '#fff',
      border: `1px solid ${BORDER}`,
      p: '14px',
      display: 'flex', flexDirection: 'column',
    }}>

      {hasBadge && (
        <Box sx={{ display: 'flex', gap: '6px', mb: '8px', flexWrap: 'wrap' }}>
          {isLowestCost    && <Badge>Lowest cost</Badge>}
          {isHighestRating && <Badge>Highest rated</Badge>}
        </Box>
      )}

      <Typography component="h2" sx={{ fontSize: '13px', fontWeight: 500, color: CHARCOAL, ...bodyFont }}>
        {bid.name || 'Untitled'}
      </Typography>
      <Typography sx={{ ...labelMono, fontSize: '10px', color: MUTED, mt: '2px', mb: '10px' }}>
        {bid.hdbdir_refno || '—'}
      </Typography>

      <Typography sx={{ fontSize: '20px', fontWeight: 300, letterSpacing: '-0.02em', color: CHARCOAL }}>
        {total != null
          ? total.toLocaleString('en-SG', { style: 'currency', currency: 'SGD', maximumFractionDigits: 0 })
          : 'N/A'}
      </Typography>
      <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, mt: '2px', mb: '12px' }}>
        Total cost (SGD)
      </Typography>

      <CostBar label="Carpentry"   value={carpentry} maxValue={total} />
      <CostBar label="Masonry"     value={masonry}   maxValue={total} />
      <CostBar label="Plmb / Elec" value={plumbing}  maxValue={total} />

      <Box sx={{
        borderTop: `0.5px solid ${BORDER}`, pt: '9px', mt: '10px',
        display: 'flex', flexDirection: 'column', gap: '5px',
      }}>
        {bid.designer_contact && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>Contact</Typography>
            <Typography sx={{ fontSize: '12px', color: CHARCOAL, ...bodyFont }}>
              {bid.designer_contact}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>Google Rating</Typography>
          <Typography sx={{ fontSize: '12px', color: CHARCOAL, ...bodyFont }}>
            {bid.google_rating != null
              ? `${Number(bid.google_rating).toFixed(1)} (${bid.google_ratingtotal ?? 0})`
              : 'N/A'}
          </Typography>
        </Box>
        {bid.insta_acct && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>Instagram</Typography>
            <Typography sx={{ fontSize: '12px', color: GREEN, ...bodyFont }}>
              @{bid.insta_acct}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>Designer</Typography>
          <Typography sx={{ fontSize: '12px', color: CHARCOAL, ...bodyFont }}>
            {bid.designer_name || 'Unknown'}
          </Typography>
        </Box>
      </Box>

      {(bid.link_floorplan || bid.link_quotation) && (
        <Box sx={{ display: 'flex', gap: '6px', mt: '9px' }}>
          {bid.link_floorplan && (
            <MuiLink href={bid.link_floorplan} target="_blank" rel="noopener noreferrer"
              underline="none" sx={{
                ...labelMono, fontSize: '9px', color: GREEN,
                border: `1px solid ${GREEN_LIGHT}`, px: '7px', py: '3px',
                '&:hover': { backgroundColor: GREEN_SUBTLE },
              }}>
              Floor plan ↗
            </MuiLink>
          )}
          {bid.link_quotation && (
            <MuiLink href={bid.link_quotation} target="_blank" rel="noopener noreferrer"
              underline="none" sx={{
                ...labelMono, fontSize: '9px', color: GREEN,
                border: `1px solid ${GREEN_LIGHT}`, px: '7px', py: '3px',
                '&:hover': { backgroundColor: GREEN_SUBTLE },
              }}>
              Quotation ↗
            </MuiLink>
          )}
        </Box>
      )}
    </Box>
  );
}

function BidsReceivedState({ bids }) {
  const [sortKey, setSortKey] = useState('cost');

  // Badges are worked out separately from the sort order, so they stay put.
  const lowestCostId = useMemo(() => {
    const valid = bids.filter((b) => b.cost_total != null);
    if (!valid.length) return null;
    return valid.reduce((a, b) => (Number(a.cost_total) <= Number(b.cost_total) ? a : b)).id;
  }, [bids]);

  const highestRatingId = useMemo(() => {
    const valid = bids.filter((b) => b.google_rating != null);
    if (!valid.length) return null;
    return valid.reduce((a, b) => (Number(a.google_rating) >= Number(b.google_rating) ? a : b)).id;
  }, [bids]);

  const sorted = useMemo(() => {
    const copy = [...bids];
    if (sortKey === 'cost') {
      copy.sort((a, b) => (Number(a.cost_total) || Infinity) - (Number(b.cost_total) || Infinity));
    } else {
      copy.sort((a, b) => (Number(b.google_rating) || 0) - (Number(a.google_rating) || 0));
    }
    return copy;
  }, [bids, sortKey]);

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: '14px', gap: 2, flexWrap: 'wrap',
      }}>
        <Typography component="h1" sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
          {bids.length} proposal{bids.length !== 1 ? 's' : ''} received
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>Sort by</Typography>
          {SORT_OPTIONS.map((opt) => (
            <Box
              key={opt.key}
              component="button"
              type="button"
              aria-pressed={sortKey === opt.key}
              onClick={() => setSortKey(opt.key)}
              sx={{
                ...labelMono, fontSize: '9px',
                color: sortKey === opt.key ? GREEN : MUTED,
                backgroundColor: sortKey === opt.key ? GREEN_FAINT : '#fff',
                border: `0.5px solid ${sortKey === opt.key ? GREEN_LIGHT : BORDER}`,
                px: '10px', py: '4px', cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
        gap: '10px',
      }}>
        {sorted.map((bid) => (
          <BidCard
            key={bid.id}
            bid={bid}
            isLowestCost={bid.id === lowestCostId}
            isHighestRating={bid.id === highestRatingId}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────
const BidManagement = () => {
  const [tenderStage,  setTenderStage]  = useState(null);   // 'PRE-TENDER' | 'POST-TENDER'
  const [bids,         setBids]         = useState([]);
  const [status,       setStatus]       = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,    setLoadError]    = useState('');
  const [publishing,   setPublishing]   = useState(false);
  const [publishError, setPublishError] = useState('');
  const [confirmOpen,  setConfirmOpen]  = useState(false);

  // The tender stage (from the household profile) and any bids, together
  const fetchData = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    try {
      const [profileData, bidsData] = await Promise.all([
        apiFetch('/api/householdprofile/my'),
        apiFetch('/api/bidmanagement/my'),
      ]);
      setTenderStage(profileData?.tenderstage || 'PRE-TENDER');
      setBids(bidsData || []);
      setStatus('ready');
    } catch (err) {
      setLoadError(friendlyMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePublish = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      await apiFetch('/api/householdprofile/tenderstage', { method: 'PATCH' });
      setConfirmOpen(false);
      await fetchData(); // shows "Your tender is live"
    } catch (err) {
      setPublishError(friendlyMessage(err, 'Something went wrong. Please try again in a moment.'));
    } finally {
      setPublishing(false);
    }
  };

  // Which screen to show:
  //   1. PRE-TENDER                     → tips + publish button
  //   2. POST-TENDER, no bids yet       → "your tender is live"
  //   3. POST-TENDER, at least one bid  → the bids, side by side
  const isPreTender  = tenderStage === 'PRE-TENDER';
  const isPostTender = tenderStage === 'POST-TENDER';
  const hasBids      = bids.length > 0;

  if (status === 'loading') {
    return <LoadingState label="Loading your proposals…" size="page" />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load Bid Management"
        message={loadError}
        onRetry={fetchData}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 3, md: '24px' } }}>
      {isPreTender && (
        <PreTenderState onRequestPublish={() => { setPublishError(''); setConfirmOpen(true); }} />
      )}

      <PublishConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handlePublish}
        publishing={publishing}
        publishError={publishError}
      />

      {isPostTender && !hasBids && <AwaitingBidsState />}

      {isPostTender && hasBids && <BidsReceivedState bids={bids} />}
    </Box>
  );
};

export default BidManagement;
